<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import CopyTrackWrapper from '@/components/CopyTrackWrapper.vue'
import GbBreadcrumbs from '@/components/GbBreadcrumbs.vue'
import LibraryContentPanel from '@/components/LibraryContentPanel.vue'
import LibraryRemoveConfirmDialog from '@/components/LibraryRemoveConfirmDialog.vue'
import NoAccessError from '@/components/NoAccessError.vue'
import ResearchCitationPanels from '@/components/ResearchCitationPanels.vue'
import ShareStrategyPanel from '@/library/ShareStrategyPanel.vue'
import StrategyBanner from './details/StrategyBanner.vue'
import StrategyNotes from './details/StrategyNotes.vue'
import StrategyPanels from './details/StrategyPanels.vue'
import StrategyRelated from './details/StrategyRelated.vue'
import StrategyResourceList from './details/StrategyResourceList.vue'
import { strategyPageSections } from './strategy_page_sections'
import { getSavedStrategy, putSavedStrategy } from '@/library/api'
import { useLibraryStore } from '@/library/library.store'
import { useUserFeedbackContext } from '@/components/useUserFeedbackContext'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { PL_SUMMARY_SAVING_STATES } from '@/presentlevels/presentlevels_constants'
import type { SavingState } from '@/presentlevels/presentlevels_types'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { setTitle } from '@/util/title'
import { VUE2_CONTAINER_WIDTHS } from '@/util/vue2_layout'
import type { SavedStrategyDetail } from './strategy_types'

const JUST_SAVED_DISPLAY_MS = 5000
const FORBIDDEN = 403

const props = defineProps<{ uuid: string }>()

const libraryStore = useLibraryStore()
const { canEditCurrentFolderContent, currentFolderUserRole, loadFolderError } = storeToRefs(libraryStore)
const { updateContext: updateFeedbackContext } = useUserFeedbackContext()

const savedStrategy = ref<SavedStrategyDetail | null>(null)
const hasAccess = ref<boolean | undefined>(undefined)
const folderLoaded = ref(false)
const editedNotes = ref('')
const notesVersion = ref(0)
const saveStatus = ref<SavingState>(PL_SUMMARY_SAVING_STATES.NO_CHANGES)
const notesRef = ref<InstanceType<typeof StrategyNotes> | null>(null)
let justSavedTimer: ReturnType<typeof setTimeout> | undefined

const sourceStrategy = computed(() => savedStrategy.value?.source_strategy)

const analytics = getAnalyticsTracker().getChildContext({
  saved_strategy_uuid: computed(() => savedStrategy.value?.uuid),
  student_uuid: computed(() => savedStrategy.value?.student_uuid),
  strategy_uuid: computed(() => sourceStrategy.value?.uuid),
  strategy_title: computed(() => sourceStrategy.value?.title),
  research_citations_count: computed(() => savedStrategy.value?.research_citations?.length ?? 0),
  is_library: true
})

// The saved strategy carries its own resources, related strategies and citations, which take
// precedence over the source strategy's, as Vue 2 merged them.
const sections = computed(() =>
  savedStrategy.value && strategyPageSections({ ...savedStrategy.value.source_strategy, ...savedStrategy.value }, false)
)

const folderName = computed(() => savedStrategy.value?.student_name ?? '')

const breadcrumbs = computed(() => [
  { label: 'Library: All Folders', to: { name: 'AllFolders' } },
  {
    label: folderName.value,
    icon: 'folder',
    to: { name: 'LibraryFolder', params: { uuid: savedStrategy.value?.student_uuid ?? '' } },
    fsExclude: true
  },
  { label: `Strategy: ${sourceStrategy.value?.title ?? ''}` }
])

function showSavedStrategy(loaded: SavedStrategyDetail) {
  savedStrategy.value = loaded
  editedNotes.value = loaded.notes ?? ''
  notesVersion.value++
  setTitle(`${loaded.source_strategy.title} | UDL Strategies`)
  updateFeedbackContext({ uuid: loaded.source_strategy.uuid, title: loaded.source_strategy.title })
}

async function loadFolder(folderUuid: string) {
  folderLoaded.value = false
  await libraryStore.fetchResourcesForFolder({ folderUUID: folderUuid, params: {} })
  folderLoaded.value = true
}

function sendViewedEvent() {
  analytics.sendEvent(
    EVENT_NAMES.SAVED_STRATEGY.VIEWED_PAGE,
    { user_folder_role: currentFolderUserRole.value || undefined },
    { cu: sourceStrategy.value?.uuid, ct: sourceStrategy.value?.title }
  )
}

async function fetchContent() {
  try {
    const loaded = await getSavedStrategy(props.uuid)
    hasAccess.value = true
    showSavedStrategy(loaded)
  } catch (error) {
    if ((error as { status_code?: number })?.status_code === FORBIDDEN) {
      hasAccess.value = false
      setTitle('UDL Strategies')
    }
    console.error('error fetching saved strategy', error)
    return
  }
  await loadFolder(savedStrategy.value!.student_uuid)
  sendViewedEvent()
}

// Save and Discard disable or hide themselves, and the notes remount, so focus moves to the
// remounted notes' Edit button rather than falling to the page body.
function focusNotesEditButton() {
  nextTick(() => notesRef.value?.focusEditButton())
}

function handleNotesEdited(notes: string) {
  editedNotes.value = notes
  saveStatus.value = notes === (savedStrategy.value?.notes ?? '')
    ? PL_SUMMARY_SAVING_STATES.NO_CHANGES
    : PL_SUMMARY_SAVING_STATES.UNSAVED_CHANGES
}

async function saveNotes() {
  if (!savedStrategy.value) return
  analytics.sendEvent(EVENT_NAMES.SAVED_STRATEGY.CLICKED_SAVE_CHANGES, { type: CONTENT_TYPES.SAVED_STRATEGY })
  saveStatus.value = PL_SUMMARY_SAVING_STATES.SAVING
  try {
    showSavedStrategy(await putSavedStrategy({ uuid: savedStrategy.value.uuid, notes: editedNotes.value }))
    saveStatus.value = PL_SUMMARY_SAVING_STATES.JUST_SAVED
    clearTimeout(justSavedTimer)
    justSavedTimer = setTimeout(() => { saveStatus.value = PL_SUMMARY_SAVING_STATES.NO_CHANGES }, JUST_SAVED_DISPLAY_MS)
    focusNotesEditButton()
  } catch (error) {
    saveStatus.value = PL_SUMMARY_SAVING_STATES.UNSAVED_CHANGES
    console.error('error saving strategy', error)
  }
}

function discardNotes() {
  editedNotes.value = savedStrategy.value?.notes ?? ''
  notesVersion.value++
  saveStatus.value = PL_SUMMARY_SAVING_STATES.NO_CHANGES
  focusNotesEditButton()
}

function recordRemoval() {
  analytics.sendEvent(EVENT_NAMES.SAVED_STRATEGY.CLICKED_MOVE_TO_TRASH, {
    uuid: savedStrategy.value?.uuid,
    type: CONTENT_TYPES.SAVED_STRATEGY
  })
}

onBeforeUnmount(() => clearTimeout(justSavedTimer))

watch(() => props.uuid, fetchContent, { immediate: true })
</script>

<template>
  <div class="container" :class="VUE2_CONTAINER_WIDTHS">
    <NoAccessError v-if="hasAccess === false" />
    <CopyTrackWrapper
      v-else-if="savedStrategy && sourceStrategy && sections"
      type="strategy"
      :title="sourceStrategy.title"
      :uuid="sourceStrategy.uuid"
      gate-copy
      :demo="sourceStrategy.demo"
    >
      <GbBreadcrumbs :items="breadcrumbs" class="mb-7 [&_ol]:rounded-none [&_ol]:py-2.5" />
      <CopyTrackWrapper type="strategy_banner" :title="sourceStrategy.title" :uuid="sourceStrategy.uuid" gate-copy>
        <StrategyBanner v-bind="sections.banner" />
      </CopyTrackWrapper>
      <div class="md:grid md:grid-cols-12 md:gap-x-[30px]">
        <div class="md:col-span-8">
          <CopyTrackWrapper type="strategy_notes" :title="sourceStrategy.title" :uuid="sourceStrategy.uuid" gate-copy>
            <StrategyNotes
              :key="notesVersion"
              ref="notesRef"
              :notes="savedStrategy.notes"
              :student-name="folderName"
              :user-name="folderName"
              collapsible
              editable
              :read-only="loadFolderError || !canEditCurrentFolderContent"
              @notes-edited="handleNotesEdited"
            />
          </CopyTrackWrapper>
          <StrategyResourceList v-bind="sections.resources" />
          <StrategyPanels v-bind="sections.implementation" />
          <StrategyPanels v-bind="sections.examples" />
          <ResearchCitationPanels v-if="sections.researchCitations" :research-citations="sections.researchCitations" />
        </div>
        <div v-if="folderLoaded" class="flex flex-col gap-5 md:col-span-4">
          <p v-if="loadFolderError" role="alert" class="rounded-sm border border-gray-400 bg-white p-4 text-text-md text-gray-900">
            We couldn’t load the folder this strategy is saved in, so its notes can’t be edited right now. Please refresh the page or try again later.
          </p>
          <template v-else>
            <LibraryContentPanel
              :content-uuid="savedStrategy.uuid"
              :content-type="CONTENT_TYPES.SAVED_STRATEGY"
              :source-content-uuid="sourceStrategy.uuid"
              :save-status="saveStatus"
              :updated-at="savedStrategy.updated_at"
              @save="saveNotes"
              @discard="discardNotes"
              @remove-confirmed="recordRemoval"
            />
            <ShareStrategyPanel
              :uuid="savedStrategy.uuid"
              :share-state="Boolean(savedStrategy.shared)"
              :share-link-state="savedStrategy.share_link ?? ''"
            />
            <LibraryRemoveConfirmDialog :group="`${savedStrategy.uuid}_${libraryStore.currentFolder?.uuid}`" />
          </template>
        </div>
      </div>
      <StrategyRelated v-if="sections.related" v-bind="sections.related" />
    </CopyTrackWrapper>
  </div>
</template>
